import {
    ActionResolution,
    EntityActionResolvedDetails,
    NameMatchSubject,
} from '@evidentid/tprm-portal-lib/models/activity-history';

/**
 * Display names for the action types that can produce an ENTITY_ACTION_RESOLVED entry, keyed by the
 * action's `$action` discriminator token (see ActionType.model.ts).
 *
 * These are deliberately flatter than the Actions tab's labels. getActionRequired() renders
 * "Entity Name Matching - Certificate Holder" and distinguishes "Exclusion Review" from
 * "Project Requirement Review" because it has the whole action payload to work from; this event
 * carries only the token, so every non-extraction criterion reads alike. That is the accepted
 * trade-off for PRODUCT-26060 - one label per action type, so the History tab needs a single filter
 * toggle. If the flattening proves too coarse, the backend can add the criterion field to the
 * details payload additively, without breaking existing rows.
 *
 * Only entity-level action types appear here. Global actions never produce this event, because their
 * resolution applies tenant-wide and belongs to no single entity's history.
 *
 * EntityNameMatchingV1 is the one exception to one-label-per-token: it covers both the entity's own name
 * and an address, and the backend distinguishes them with nameMatchSubject. See NAME_MATCH_SUBJECT_LABELS.
 */
export const ACTION_TYPE_LABELS: Record<string, string> = {
    EntityNameMatchingV1: 'Entity name matching',
    EvaluatedEntityMappingV1: 'Collateral mapping',
    NonExtractionCriterionResolutionV1: 'Criterion review',
    AdditionalInsuredEndorsementManuscriptCategorization: 'Endorsement review',
};

/**
 * Overrides for entity name matching, which the backend disambiguates with nameMatchSubject. Without this,
 * a reviewer approving a certificate-holder address would read "Entity name matching action was accepted",
 * which names the wrong thing rather than merely being vague.
 */
const NAME_MATCH_SUBJECT_LABELS: Record<NameMatchSubject, string> = {
    ENTITY_NAME: 'Entity name matching',
    ADDRESS: 'Address matching',
};

const RESOLUTION_LABELS: Record<ActionResolution, string> = {
    ACCEPTED: 'accepted',
    REJECTED: 'rejected',
};

/**
 * Human-readable summary for an ENTITY_ACTION_RESOLVED entry, e.g.
 * "Entity name matching action was accepted".
 *
 * Degrades in two steps rather than one, because the two halves fail independently:
 * an action type this app does not recognise still has a meaningful outcome ("An action was
 * accepted"), whereas an unrecognised outcome makes the whole sentence untrustworthy and falls back
 * to naming the event only. The backend can ship a new action type before this app knows it, so the
 * first case is expected rather than exceptional.
 */
export function formatEntityActionResolvedSummary(
    details: EntityActionResolvedDetails | null | undefined,
): string {
    const outcome = details?.resolution ? RESOLUTION_LABELS[details.resolution] : undefined;
    if (!outcome) {
        return 'An action was resolved';
    }
    const subjectLabel = details?.nameMatchSubject
        ? NAME_MATCH_SUBJECT_LABELS[details.nameMatchSubject]
        : undefined;
    const actionLabel = subjectLabel
        ?? (details?.actionType ? ACTION_TYPE_LABELS[details.actionType] : undefined);
    return actionLabel ? `${actionLabel} action was ${outcome}` : `An action was ${outcome}`;
}
